import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useDeepCompareEffect } from 'react-use';
import { useDispatch } from 'react-redux';
import { useTranslate, useCurrentUser, useShallowEqualSelector } from '@hooks';
import { fetchCampaigns } from '@actions';
import { getNameWithId } from '@dotone/react-ui-core/utils';
import { getValidStatus } from '@utils';
import { approvalStatusesByStatus } from '@constants';
import { Modal, FlexContainer, DetailsBlock, Text } from '@dotone/react-ui-core';
import CampaignTable from './CampaignTable';
import {
  formatAffiliate,
  formatNetworkPerformance,
  formatNetworkHistory,
  formatInternalNotes,
} from '../Affiliates/formatters';
import { formatSiteInfos } from '../formatters';

const CampaignTableModal = ({ affiliate, isOpen, onToggle }) => {
  const { t, tHint } = useTranslate('affiliate', 'model');
  const dispatch = useDispatch();
  const router = useRouter();
  const { locale, currency, currentUser } = useCurrentUser();
  const { data, pagination, isLoading } = useShallowEqualSelector(
    ({ campaignList }) => campaignList
  );
  const validStatus = getValidStatus(router.query.tab);
  const approvalStatuses = validStatus ? approvalStatusesByStatus[validStatus] : undefined;
  const [tableProps, setTableProps] = useState({
    page: 1,
    perPage: 10,
    filters: { approvalStatuses },
  });
  const { page, perPage, filters } = tableProps;
  const affiliateId = affiliate?.id;
  const query = {
    locale,
    currency,
    page,
    perPage,
    ...filters,
    affiliateIds: affiliateId,
  };

  useDeepCompareEffect(() => {
    if (isOpen && affiliateId && currentUser.id) {
      dispatch(fetchCampaigns(query));
    }
  }, [dispatch, isOpen, affiliateId, currentUser.id, query]);

  return (
    <Modal
      title={t('affiliate:campaignModal.title', {
        affiliate: affiliate?.canAccess ? getNameWithId(affiliate) : affiliate?.id,
      })}
      size="xlg"
      isOpen={isOpen}
      onToggle={onToggle}
      footerVariant="close"
      scrollable={false}
    >
      {affiliate && (
        <FlexContainer justify="between" align="start" className="mb-3 w-100">
          <DetailsBlock className="p-2" style={{ flex: 1 }}>
            <Text weight="bold" className="mb-3 text-center">
              {t('common:model.affiliate')}
            </Text>
            {formatAffiliate(affiliate, true, 'campaign')}
          </DetailsBlock>
          <DetailsBlock className="p-2" style={{ flex: 1 }}>
            <Text weight="bold" className="mb-3">
              {t('siteInfos')}
            </Text>
            {formatSiteInfos(affiliate.siteInfos || [], affiliate)}
          </DetailsBlock>
          {currentUser.canAccessAffiliate && (
            <DetailsBlock className="p-2" style={{ flex: 1 }}>
              <Text weight="bold" className="mb-3">
                {t('networkPerformance')}
              </Text>
              {formatNetworkPerformance(affiliate, t, tHint, 'detail')}
            </DetailsBlock>
          )}
          {currentUser.canAccessAffiliate && (
            <DetailsBlock className="p-2" style={{ flex: 1 }}>
              <Text weight="bold" className="mb-3">
                {t('networkHistory')}
              </Text>
              {formatNetworkHistory(affiliate, t)}
            </DetailsBlock>
          )}
          <DetailsBlock className="p-2" style={{ flex: 1 }}>
            <Text weight="bold" className="mb-3">
              {t('internalNotes')}
            </Text>
            {formatInternalNotes(affiliate, t)}
          </DetailsBlock>
        </FlexContainer>
      )}
      <CampaignTable
        tableProps={tableProps}
        loading={isLoading}
        data={data}
        pagination={pagination}
        onTableChange={setTableProps}
        responsive={false}
      />
    </Modal>
  );
};

export default CampaignTableModal;
